import * as React from 'react';
import {Mutation} from 'react-apollo';
import {Helmet} from 'react-helmet';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';
// @ts-expect-error
import YouTube from 'react-youtube';

import {formEncodedApi} from 'nutshell-core/api';
import {Routing} from 'nutshell-core/routing';

import {CTABanner} from 'shells/cta-banner/cta-banner';
import {headTitle} from 'shells/document/head-title';
// @ts-expect-error - imported from .js/.jsx file
import {CloseIcon} from 'shells/icon/svg/close-icon';
// @ts-expect-error - imported from .js/.jsx file
import {PlayButtonIcon} from 'shells/icon/svg/play-button-icon';
import {MainContentContainer} from 'shells/layout/main-content-container';
import {ModalLightbox} from 'shells/modal/modal-lightbox';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {
    UpdateEmailSequenceTemplate as UpdateEmailSequenceTemplateMutation,
    UpdateEmailSequenceTemplateVariables as UpdateEmailSequenceTemplateMutationVariables,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';
import {bustManagedCollectionCache} from '../../../util/bust-managed-collection-cache';

import UPDATE_EMAIL_SEQUENCE_TEMPLATE from '../graphql/mutations/update-email-sequence-template.graphql';

import {EmailTemplateDesignerModal} from './email-template-designer-modal';
import {EmailTemplatesPageHeader} from './email-templates-page-header';
import type {SequenceTemplateType} from './template-list/constants';
import {TemplateList} from './template-list/template-list';

import styles from './email-templates-page.module.css';

const VIDEO_ID = '3ackKdxgkFY';

type OwnProps = ContextRouter;
type Props = ContextRouter & {
    renameSequenceTemplate: (sequenceTemplateId: string, newName: string) => Promise<any>;
};

type State = {
    isRenameProcessing: boolean;
    sequenceTemplateToRenameId?: string;
    sequenceTemplateCurrentName?: string;
    shouldShowVideoBanner: boolean;
    isVideoModalOpen: boolean;
};

class EmailTemplatesComponent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isRenameProcessing: false,
            sequenceTemplateToRenameId: undefined,
            sequenceTemplateCurrentName: undefined,
            shouldShowVideoBanner: NutClientConfig.shouldShowEmailDripVideoBanner,
            isVideoModalOpen: false,
        };
    }

    render() {
        const {sequenceTemplateToRenameId} = this.state;

        return (
            <React.Fragment>
                <Helmet>
                    <title>{headTitle(this.getHeaderTitle())}</title>
                </Helmet>
                <MainContentContainer whiteBackground={true}>
                    <EmailTemplatesPageHeader
                        currentPath={this.props.location.pathname}
                        title={this.getHeaderTitle()}
                        subTitle={this.getHeaderSubtitle()}
                    />
                    <TemplateList
                        type={this.getTypeFromUrl()}
                        onSequenceRename={this.showSequenceRenamer}
                    />
                    <EmailTemplateDesignerModal
                        isOpen={this.shouldShowModal()}
                        sequenceTemplateId={this.getSequenceId()}
                        sentEmailId={this.getSentEmailId()}
                        onClose={this.hideSequenceCreator}
                        onChildTemplateSaved={this.goToUserTemplates}
                    />
                    {sequenceTemplateToRenameId ? (
                        <SingleInputModal
                            title='Rename email template'
                            subtitle='This is for you and your team.  People you email won’t see it.'
                            buttonText='Rename'
                            initialValue={this.state.sequenceTemplateCurrentName}
                            isSaving={this.state.isRenameProcessing}
                            onSave={(newName: string) =>
                                this.handleRenameSequenceTemplate(
                                    sequenceTemplateToRenameId,
                                    newName
                                )
                            }
                            onClose={this.hideSequenceRenamer}
                        />
                    ) : null}
                    {this.state.shouldShowVideoBanner ? (
                        <div className={styles['cta-banner-container']}>
                            <CTABanner
                                icon={PlayButtonIcon}
                                title='Personal email sequences!'
                                text='Send automated follow-ups, track reply and click rates, and more.'
                                buttonText='Watch the introduction video'
                                // @ts-expect-error
                                onDismiss={this.handleHideVideoBanner}
                                onPrimaryAction={this.openVideoModal}
                            />
                        </div>
                    ) : null}
                    {this.state.isVideoModalOpen ? (
                        <ModalLightbox onClose={this.closeVideoModal}>
                            <div
                                onClick={this.closeVideoModal}
                                className={styles['modal-container']}
                            >
                                <button onClick={this.closeVideoModal} data-tip='Close'>
                                    <CloseIcon size={17} />
                                </button>
                                <YouTube
                                    videoId={VIDEO_ID}
                                    opts={{
                                        height: '414',
                                        width: '736',
                                        playerVars: {
                                            autoplay: 1,
                                            list: 'PLoLQikdPun7d2d3fahIfUni9854Zm1DxS',
                                            listType: 'playlist',
                                            modestbranding: 1,
                                            rel: 0,
                                        },
                                    }}
                                />
                            </div>
                        </ModalLightbox>
                    ) : undefined}
                </MainContentContainer>
            </React.Fragment>
        );
    }

    hideSequenceCreator = () => {
        this.props.history.replace(`${this.props.location.pathname.replace(/(\/new|\/edit)/, '')}`);
    };

    goToUserTemplates = () => {
        // old templates page, no need to update the URL
        this.props.history.replace('/email/templates/yours');
    };

    handleHideVideoBanner = () => {
        // Toggle setting in the DB
        formEncodedApi.post('/my-account/save-user-setting.json', {
            setting: 'has_dismissed-drip-video-10372',
            value: 1,
        });

        // Hide the banner locally
        this.setState({shouldShowVideoBanner: false});

        // Set our window variable so that they can't get it back up
        // if the component were to re-mount
        NutClientConfig.shouldShowEmailDripVideoBanner = false;
    };

    openVideoModal = () => {
        this.setState({isVideoModalOpen: true});
        analyticsClient.track(analyticsClient.EVENTS.TRACK_VIDEO_POPUP_VIEWED, {
            videoId: VIDEO_ID,
        });
    };

    closeVideoModal = () => {
        this.setState({isVideoModalOpen: false});
    };

    showSequenceRenamer = (sequenceTemplateId: string, currentName: string) => {
        this.setState({
            sequenceTemplateToRenameId: sequenceTemplateId,
            sequenceTemplateCurrentName: currentName,
        });
    };

    hideSequenceRenamer = () => {
        this.setState({
            sequenceTemplateToRenameId: undefined,
        });
    };

    shouldShowModal = () => {
        return (
            this.props.match.params.action === 'new' || this.props.match.params.action === 'edit'
        );
    };

    getSequenceId = () => {
        const params = Routing.deparam(this.props.location.search);

        return params.sequenceId;
    };

    getSentEmailId = () => {
        const params = Routing.deparam(this.props.location.search);

        return params.sentEmailId;
    };

    getTypeFromUrl = (): SequenceTemplateType => {
        if (this.props.match.params.type === 'shared') return 'sharedTemplates';
        if (this.props.match.params.type === 'archived') return 'archivedTemplates';

        return 'userTemplates';
    };

    getHeaderTitle = () => {
        if (this.props.match.params.type === 'shared') return 'Shared email templates';
        if (this.props.match.params.type === 'archived') return 'Archived email templates';

        return 'Your email templates';
    };

    getHeaderSubtitle = () => {
        if (this.props.match.params.type === 'shared')
            return 'Email templates your team has shared';
        if (this.props.match.params.type === 'archived')
            return 'All scheduled emails will still finish sending, but will no longer be available for future use.';

        return 'Save time and automate your follow-up.';
    };

    handleRenameSequenceTemplate = (sequenceTemplateId: string, newName: string) => {
        this.setState({isRenameProcessing: true});
        this.props.renameSequenceTemplate(sequenceTemplateId, newName).then(() => {
            bustManagedCollectionCache('emailSequenceTemplates');

            this.setState({
                isRenameProcessing: false,
                sequenceTemplateToRenameId: undefined,
            });
        });
    };
}

class RenameMutation extends Mutation<
    UpdateEmailSequenceTemplateMutation,
    UpdateEmailSequenceTemplateMutationVariables
> {}

export const EmailTemplatesPage = (props: OwnProps) => (
    <RenameMutation mutation={UPDATE_EMAIL_SEQUENCE_TEMPLATE}>
        {(rename) => (
            <EmailTemplatesComponent
                // @ts-expect-error
                renameSequenceTemplate={(sequenceTemplateId, newName) =>
                    rename({
                        variables: {
                            emailSequenceTemplate: {
                                emailSequenceTemplate: {
                                    id: sequenceTemplateId,
                                    name: newName,
                                },
                            },
                        },
                    })
                }
                {...props}
            />
        )}
    </RenameMutation>
);
